import React, { useEffect, useMemo, useRef, useState } from "react";

import { Card, CardHeader, CardBody, Button } from "reactstrap";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import Toggle from "modules/5_view_helpers/web/Toggle";

import CurrentQASlidingPane from "./index/CurrentQASlidingPane";
import AreasSlidingPane from "./index/AreasSlidingPane";
import TrimbleConnectSlidingPane from "./index/TrimbleConnectSlidingPane";
import BoltQaPane from "./index/BoltQaPane";
import BoltListPane from "./index/BoltListPane";
import { selectModelGUIDS } from "./helpers";
import { getProjectIfcModels } from "modules/project/ifc_models/actions";

const IfcViewer = React.lazy(() => import("modules/project/ifc_models/web/IfcViewer"));

const QAIndex = (props) => {
  const dispatch = useDispatch();
  const current_user = useSelector((state) => state.current_user);
  const projects = useSelector((state) => state.projects);
  const project_ifc_models = useSelector((state) => state.project_ifc_models);
  const ThemeOptions = useSelector((state) => state.ThemeOptions);
  const fullscreen = ThemeOptions.fullscreen || ThemeOptions.fullscreenEnabled;
  const current_project = projects[current_user?.current_project_id];

  const models = useMemo(
    () => _.sortBy(_.filter(project_ifc_models, { project_id: current_project?.id }), "name"),
    [project_ifc_models, current_project?.id]
  );
  const viewerModels = useMemo(
    () =>
      _.compact(
        _.map(models, (model) =>
          model.file_url ? { id: model.id, fileUrl: model.file_url, modelName: model.name } : null
        )
      ),
    [models]
  );

  const [showInNewWindow, setShowInNewWindow] = useState(false);
  const [InfoOpen, setInfoOpen] = useState(false);
  const [QAOpen, setQAOpen] = useState(false);
  const [AreasOpen, setAreasOpen] = useState(false);
  const [BoltQAOpen, setBoltQAOpen] = useState(false);
  const [BoltListOpen, setBoltListOpen] = useState(false);
  const [selectedObjects, setSelectedObjects] = useState([]);

  const [absoluteOffsetTop, setAbsoluteOffsetTop] = useState(0);
  const viewerRef = useRef(null);

  useEffect(() => {
    if (current_project?.id) {
      dispatch(getProjectIfcModels(current_project.id));
    }
  }, [current_project?.id, dispatch]);

  useEffect(() => {
    const updateOffset = () => {
      if (viewerRef.current) {
        const absoluteTop = viewerRef.current.getBoundingClientRect().top + window.scrollY;
        setAbsoluteOffsetTop(absoluteTop);
      }
    };

    updateOffset();
    window.addEventListener("scroll", updateOffset);
    window.addEventListener("resize", updateOffset);
    return () => {
      window.removeEventListener("scroll", updateOffset);
      window.removeEventListener("resize", updateOffset);
    };
  }, [fullscreen, models]);

  return (
    <Card>
      <CardHeader>
        Quality Assurance
        <Button
          onClick={() => {
            setBoltQAOpen(!BoltQAOpen);
          }}
          style={{ marginLeft: "250px" }}
          color="success"
          id="BoltQA"
          disabled={_.isEmpty(selectedObjects)}
        >
          Do Bolt QA
        </Button>
        <Button
          onClick={() => {
            setBoltListOpen(!BoltListOpen);
          }}
          className="ml-2"
          color="secondary"
          id="BoltList"
        >
          Show Bolt List
        </Button>
        {_.isNotEmpty(selectedObjects) && (
          <span className="ml-3 text-muted">
            {_.size(selectedObjects)} selected
            {selectedObjects[0]?.guid ? ` — ${selectedObjects[0].guid}` : ""}
          </span>
        )}
        <Toggle
          containerStyle={{ paddingLeft: "200px" }}
          containerClassName="ml-2 mt-3 text-right"
          label="Popout"
          checked={showInNewWindow}
          color="success"
          onClick={() => {
            setShowInNewWindow(!showInNewWindow);
          }}
        />
      </CardHeader>
      <CardBody>
        <div ref={viewerRef} style={{ height: `calc(95vh - ${absoluteOffsetTop}px)` }}>
          <React.Suspense fallback={<div className="text-center p-4">Loading viewer…</div>}>
            <IfcViewer models={viewerModels} projectId={current_project?.id} onSelectionChanged={setSelectedObjects} />
          </React.Suspense>
        </div>
      </CardBody>
      <TrimbleConnectSlidingPane
        isOpen={InfoOpen}
        selectedObjects={selectedObjects}
        toggle={() => {
          setInfoOpen(!InfoOpen);
        }}
      />

      {QAOpen && (
        <CurrentQASlidingPane
          onSelected={(selectedAssemblies) => {
            const uncompressedGuids = _.map(selectedAssemblies, "guid");
            selectModelGUIDS(uncompressedGuids);
          }}
          isOpen={QAOpen}
          project_id={current_project?.id}
          toggle={() => {
            setQAOpen(!QAOpen);
          }}
          showInNewWindow={showInNewWindow}
          {...props}
        />
      )}

      {AreasOpen && (
        <AreasSlidingPane
          isOpen={AreasOpen}
          project_id={current_project?.id}
          toggle={() => {
            setAreasOpen(!AreasOpen);
          }}
          showInNewWindow={showInNewWindow}
          {...props}
        />
      )}

      {BoltQAOpen && (
        <BoltQaPane
          isOpen={BoltQAOpen}
          project_id={current_project?.id}
          toggle={() => {
            setBoltQAOpen(!BoltQAOpen);
          }}
          showInNewWindow={showInNewWindow}
          selectedObjects={selectedObjects}
          {...props}
        />
      )}
      {BoltListOpen && (
        <BoltListPane
          isOpen={BoltListOpen}
          project={current_project}
          toggle={() => {
            setBoltListOpen(!BoltListOpen);
          }}
          showInNewWindow={showInNewWindow}
          {...props}
        />
      )}
    </Card>
  );
};

export default QAIndex;
